import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { MultiSectionsDisplay } from 'src/components/Events/VenueMap';
import { EventVenueMap } from 'src/components/Events/VenueMap/EventVenueMap';
import { VenueMapContentProps } from 'src/components/Events/VenueMap/VenueMapProps';
import { Content } from 'src/contexts/ContentContext';
import { Button, Stack } from 'src/core/ui';
import { ChangeZoneMapSectionGroupDialog } from 'src/dialogs/ChangeZoneMapSectionGroupDialog';
import { useAutoCompMapSectionHandler } from 'src/hooks/useAutoCompMapSectionHandler';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature, SectionSectionGroup } from 'src/WebApiController';

import { EventVenueMapFormType } from '../EventVenueMapModal';
import { useSectionGroupOperations } from './v2/SectionGroupPanelV2.hooks';
import { ZoneMapSelectedSectionsActions } from './v2/ZoneMapSelectedSectionsActions';

export const EventVenueMapContent = ({
  createGetColor,
}: {
  createGetColor: (
    sectionSectionGroupLookup?: {
      [key: string]: SectionSectionGroup;
    } | null,
    selectedSectionIds?: number[]
  ) => NonNullable<VenueMapContentProps['getColor']>;
}) => {
  const hasZoneMapV2Feature = useUserHasFeature(Feature.ZoneMapV2);
  const changeSectionGroupDialog = useBasicDialog();
  const onLaunchingSingleSectionDialog = useCallback(
    () => changeSectionGroupDialog.launchDialog(),
    [changeSectionGroupDialog]
  );

  const {
    selectedSectionIds,
    selectedSectionData,
    onSectionClicked,
    onResetSections,
    onToggleMirrors,
    onSelectedSectionsChange,
  } = useAutoCompMapSectionHandler(onLaunchingSingleSectionDialog);

  const { watch, setValue } = useFormContext<EventVenueMapFormType>();
  const sectionSectionGroupLookup = watch('sectionSectionGroupLookup');
  const customSectionGroups = watch('customSectionGroups');

  const { onUnassignSections, onCreateSectionGroup, sortabledSectionGroups } =
    useSectionGroupOperations(sectionSectionGroupLookup, customSectionGroups);

  const zonedSectionIds = useMemo(() => {
    return new Set(
      Object.keys(sectionSectionGroupLookup)
        .map((id) => parseInt(id))
        .filter((id): id is number => !isNaN(id))
    );
  }, [sectionSectionGroupLookup]);

  const onSectionZoneChanged = useCallback(
    (newSectionSectionGroup: SectionSectionGroup, sectionIds: number[]) => {
      const sectionGroupIdMap = sectionSectionGroupLookup;

      const newSectionGroupIdMap = {
        ...sectionGroupIdMap,
        ...sectionIds.reduce(
          (acc: { [key: string]: SectionSectionGroup }, sectionId) => {
            acc[sectionId] = newSectionSectionGroup;
            return acc;
          },
          {}
        ),
      };

      setValue('sectionSectionGroupLookup', newSectionGroupIdMap);
      onResetSections();

      changeSectionGroupDialog.closeDialog();
    },
    [
      sectionSectionGroupLookup,
      setValue,
      onResetSections,
      changeSectionGroupDialog,
    ]
  );

  const onCreateNewSectionGroup = useCallback(
    (
      sectionIds: number[],
      sectionGroupAreaName: string,
      sectionGroupColor: string
    ) => {
      onCreateSectionGroup(sectionIds, sectionGroupAreaName, sectionGroupColor);
      onResetSections();
    },
    [onCreateSectionGroup, onResetSections]
  );

  const onUnassignSelectedSections = useCallback(
    (sectionIds: number[]) => {
      onUnassignSections(sectionIds);
      onResetSections();
    },
    [onResetSections, onUnassignSections]
  );

  return (
    <>
      <EventVenueMap
        markedSectionIds={selectedSectionIds}
        onSectionClicked={onSectionClicked}
        onToggleMirrors={onToggleMirrors}
        setSelectedSections={onSelectedSectionsChange}
        showDefaultMapColors={true}
        getColor={createGetColor(sectionSectionGroupLookup, selectedSectionIds)}
        alwaysShowStaticInfo={hasZoneMapV2Feature}
        staticInfoContent={
          hasZoneMapV2Feature ? (
            <ZoneMapSelectedSectionsActions
              zonedSectionIds={zonedSectionIds}
              selectedSections={selectedSectionData ?? []}
              onResetSections={onResetSections}
              onUnassignSections={onUnassignSelectedSections}
              onCreateSectionGroup={onCreateNewSectionGroup}
              onUpdateZoneGroup={changeSectionGroupDialog.launchDialog}
              sectionGroups={sortabledSectionGroups}
            />
          ) : selectedSectionData ? (
            <Stack direction="row" gap="l" alignItems="center">
              <MultiSectionsDisplay
                sections={selectedSectionData}
                onClearSelection={onResetSections}
              />
              <Button
                size="md"
                onClick={() => changeSectionGroupDialog.launchDialog()}
              >
                <Content id={ContentId.EditZone} />
              </Button>
            </Stack>
          ) : undefined
        }
        isRelocatedStaticInfo={!hasZoneMapV2Feature}
        staticInfoContentPosition={hasZoneMapV2Feature ? 'bottom' : undefined}
      />
      <ChangeZoneMapSectionGroupDialog
        {...changeSectionGroupDialog.dialogProps}
        selectedSections={selectedSectionData ?? []}
        sectionIdMapping={sectionSectionGroupLookup}
        sectionSectionGroupLookup={sectionSectionGroupLookup}
        sectionGroups={sortabledSectionGroups}
        onOkay={onSectionZoneChanged}
        onCancel={changeSectionGroupDialog.closeDialog}
      />
    </>
  );
};
